// Client-side video frame capture (no ffmpeg needed).
// Works because our videos are same-origin (blob URLs or /api/files), so the
// canvas is not tainted and toBlob() succeeds.

const isVideoFile = (file) =>
  (file.type && file.type.startsWith("video/")) ||
  /\.(mp4|webm|mov|m4v|ogv)$/i.test(file.name || "");

export const isVideo = isVideoFile;

const captureCurrentFrame = (video) =>
  new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85);
  });

// Load a local File into an off-DOM <video> and capture its first usable frame.
// Returns { blob, width, height, duration }.
export const captureFirstFrame = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.src = url;
    let done = false;
    const finish = async () => {
      if (done) return;
      done = true;
      try {
        const blob = await captureCurrentFrame(v);
        const meta = { width: v.videoWidth, height: v.videoHeight, duration: v.duration };
        URL.revokeObjectURL(url);
        resolve({ blob, ...meta });
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    v.onloadeddata = () => {
      const t = Math.min(0.1, (v.duration || 1) / 2);
      try { v.currentTime = t; } catch (e) { finish(); }
    };
    v.onseeked = finish;
    v.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read video")); };
    // safety timeout
    setTimeout(() => finish(), 4000);
  });

// Capture a frame from an already-rendered, same-origin <video> element at its
// current playback position. Returns a JPEG Blob.
export const captureFrameFromEl = (video) => captureCurrentFrame(video);
